import type { ApiListingRequest, ApiResponse } from '@bitstopco/bitstop-theme';

import { getLegacyPaginatedPayload } from '@/helpers/api';

import api from '@/store/api';
import { athena, operator } from '@/store/api/instances';

import { useAppDispatch } from '@/store';
import type {
  Affiliate,
  AffiliateAtm,
  AffiliateAtmListView,
  AffiliateUser,
  PostCreateAffiliateOrganizationRequest,
  PostCreateAffiliateOrganizationResponse,
  PostCreateAffiliateUserRequest,
  RemoveAffiliateMachinesRequest,
} from '@/types';

type AffiliateApiListingRequest = ApiListingRequest & {
  affiliateId: string | null;
};

/**
 * Affiliates RTK-Query API
 * For more information about RTK-Query see
 * * https://redux-toolkit.js.org/rtk-query/overview
 */
export const affiliatesApi = api.injectEndpoints({
  endpoints: (build) => ({
    getAffiliates: build.query<ApiResponse<Affiliate[]>, ApiListingRequest>({
      query: (params) =>
        athena({
          params: getLegacyPaginatedPayload(params),
          url: 'affiliates',
        }),
      providesTags: ['Affiliates'],
    }),
    getAffiliateUsers: build.query<ApiResponse<AffiliateUser[]>, AffiliateApiListingRequest>({
      query: ({ affiliateId, ...params }) =>
        athena({
          params: getLegacyPaginatedPayload(params),
          url: `affiliate/${affiliateId}/users`,
        }),
      providesTags: ['AffiliateUsers'],
    }),
    // Browses ALL of the operator's machines (unscoped by affiliate) -- used by the
    // "Add Machines" modal to pick machines to attach to an affiliate.
    getAffiliateMachines: build.query<ApiResponse<AffiliateAtm[]>, AffiliateApiListingRequest>({
      query: ({ affiliateId, ...params }) =>
        athena({
          params: {
            ...getLegacyPaginatedPayload(params),
            query: JSON.stringify([
              {
                property: 'affiliate_id',
                operator: 'equal',
                value: affiliateId,
              },
            ]),
          },
          url: 'atms/recommended_replacement',
        }),
      providesTags: ['AffiliateMachines'],
    }),
    // Lists the machines already belonging to one specific affiliate, including live cash --
    // used by the affiliate detail "Machines" tab. Staff-scoped (by operator + affiliate id),
    // unlike the self-service-only v2/Affiliate/atms endpoint.
    getAffiliateAtms: build.query<ApiResponse<AffiliateAtmListView[]>, AffiliateApiListingRequest>({
      query: ({ affiliateId, ...data }) =>
        athena({
          data,
          method: 'POST',
          url: `v2/atms/affiliate/${affiliateId}`,
        }),
      providesTags: ['AffiliateMachines'],
    }),
    postCreateAffiliateOrganization: build.mutation<
      PostCreateAffiliateOrganizationResponse,
      PostCreateAffiliateOrganizationRequest
    >({
      query: (data) =>
        operator({
          data,
          method: 'POST',
          url: 'affiliate',
          showError: true,
        }),
    }),
    // Remove at the auth0 cutover, replaced by postCreateAffiliateUserV2
    postCreateAffiliateUser: build.mutation<void, PostCreateAffiliateUserRequest>({
      query: (data) =>
        operator({
          data,
          method: 'POST',
          url: 'affiliate/users',
          showError: true,
        }),
    }),
    postCreateAffiliateUserV2: build.mutation<void, PostCreateAffiliateUserRequest>({
      query: ({ affiliate_id, ...data }) =>
        athena({
          data,
          method: 'POST',
          url: `v2/UserAdministration/affiliates/${affiliate_id}`,
          showError: true,
        }),
    }),
    addAffiliateMachines: build.mutation<void, RemoveAffiliateMachinesRequest>({
      query: ({ affiliate_id, ...data }) =>
        operator({
          data,
          method: 'POST',
          url: `affiliate/${affiliate_id}/atms`,
          showError: true,
        }),
      invalidatesTags: ['AffiliateMachines'],
    }),
    removeAffiliateMachines: build.mutation<void, RemoveAffiliateMachinesRequest>({
      query: ({ affiliate_id, ...data }) =>
        operator({
          data,
          method: 'DELETE',
          url: `affiliate/${affiliate_id}/atms`,
          showError: true,
        }),
      invalidatesTags: ['AffiliateMachines'],
    }),
  }),
});

export const {
  // Queries
  useGetAffiliatesQuery,
  useGetAffiliateUsersQuery,
  useGetAffiliateMachinesQuery,
  useGetAffiliateAtmsQuery,

  // Mutations
  usePostCreateAffiliateOrganizationMutation,
  usePostCreateAffiliateUserMutation,
  usePostCreateAffiliateUserV2Mutation,
  useAddAffiliateMachinesMutation,
  useRemoveAffiliateMachinesMutation,
} = affiliatesApi;

export const {
  endpoints: {
    getAffiliates,
    getAffiliateUsers,
    getAffiliateMachines,
    getAffiliateAtms,
    postCreateAffiliateOrganization,
    postCreateAffiliateUser,
    postCreateAffiliateUserV2,
    addAffiliateMachines,
    removeAffiliateMachines,
  },
} = affiliatesApi;

/**
 * The organization was created but its first user was not. There is no endpoint to
 * roll the organization back, so retrying the whole form would create a second one;
 * callers send the operator on to add the user to this organization instead.
 */
export class AffiliateUserCreationError extends Error {
  affiliateId: string;

  constructor(affiliateId: string) {
    super('The affiliate organization was created, but its first user could not be added');
    this.affiliateId = affiliateId;
  }
}

export const useAffiliate = () => {
  const dispatch = useAppDispatch();

  const [createAffiliateOrganization] = usePostCreateAffiliateOrganizationMutation();
  const [createAffiliateUser] = usePostCreateAffiliateUserMutation();
  const [createAffiliateUserV2] = usePostCreateAffiliateUserV2Mutation();

  const buildCreateAffiliateOrg =
    (createUser: typeof createAffiliateUser) =>
    async ({
      email,
      phone,
      country_code,
      first_name,
      last_name,
      ...organization
    }: PostCreateAffiliateOrganizationRequest &
      Omit<PostCreateAffiliateUserRequest, 'affiliate_id'>) => {
      const {
        data: { id: affiliate_id },
      } = await createAffiliateOrganization(organization).unwrap();

      // The organization is not rolled back if the user fails, so it has to show up either way
      dispatch(affiliatesApi.util.invalidateTags(['Affiliates']));

      try {
        await createUser({
          email,
          phone,
          last_name,
          first_name,
          affiliate_id,
          country_code,
        }).unwrap();
      } catch {
        throw new AffiliateUserCreationError(affiliate_id);
      }
    };

  return {
    // Remove at the auth0 cutover, replaced by createAffiliateOrgV2
    createAffiliateOrg: buildCreateAffiliateOrg(createAffiliateUser),
    // The first user is created through athena, which also provisions their auth0 identity
    createAffiliateOrgV2: buildCreateAffiliateOrg(createAffiliateUserV2),
  };
};
